<script setup>
import { ref, computed, watch } from 'vue'
import { XCircle, Loader2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  features: {
    type: Array,
    default: () => []
  },
  isUploading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'cancel', 'retry', 'download-failed'])

// Upload states
const uploadStates = ref(new Map()) // feature.id -> { status, error, progress }
const startTime = ref(null)
const endTime = ref(null)

// Store initial count to fix progress calculation bug
const initialTotalCount = ref(null)

// Computed properties
const totalCount = computed(() => {
  // Use initial count if it has been explicitly set, otherwise current features length
  return initialTotalCount.value !== null ? initialTotalCount.value : props.features.length
})
const uploadedCount = computed(() =>
  Array.from(uploadStates.value.values()).filter(state => state.status === 'success').length
)
const failedCount = computed(() =>
  Array.from(uploadStates.value.values()).filter(state => state.status === 'error').length
)
const uploadingCount = computed(() =>
  Array.from(uploadStates.value.values()).filter(state => state.status === 'uploading').length
)
const pendingCount = computed(() =>
  totalCount.value - uploadedCount.value - failedCount.value - uploadingCount.value
)

const overallProgress = computed(() => {
  if (totalCount.value === 0) return 0
  return Math.round((uploadedCount.value / totalCount.value) * 100)
})

const isComplete = computed(() => {
  return uploadedCount.value + failedCount.value === totalCount.value
})




// Methods
const initializeUpload = () => {
  startTime.value = Date.now()
  endTime.value = null
  uploadStates.value.clear()

  // Store initial count to prevent progress calculation issues
  initialTotalCount.value = props.features.length

  // Initialize all features as pending
  props.features.forEach(feature => {
    uploadStates.value.set(feature.properties.local_uuid, {
      status: 'pending',
      progress: 0,
      error: null,
      name: feature.properties?.name || feature.properties?.fileName || 'Unnamed'
    })
  })
}

const updateFeatureStatus = (featureId, status, error = null, progress = 0) => {
  const currentState = uploadStates.value.get(featureId) || {}
  uploadStates.value.set(featureId, {
    ...currentState,
    status,
    error,
    progress
  })

  // Check if upload is complete
  if (isComplete.value && !endTime.value) {
    endTime.value = Date.now()
  }
}



const handleClose = () => {
  if (props.isUploading) {
    emit('cancel')
  } else {
    emit('close')
  }
}


// Watch for upload start/stop
watch(() => props.isUploading, (isUploading) => {
  if (isUploading) {
    initializeUpload()
  }
})

// Expose methods for parent component
defineExpose({
  updateFeatureStatus
})
</script>

<template>
  <Dialog :open="open" @update:open="handleClose">
    <DialogContent class="max-w-2xl max-h-[80vh]">
      <DialogHeader>
        <DialogTitle>Upload Progress</DialogTitle>
      </DialogHeader>

      <div class="space-y-4">
        <!-- Simplified Progress -->
        <div class="space-y-2">
          <!-- Native Progress Bar -->
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
              :style="{ width: overallProgress + '%' }"
            ></div>
          </div>
          <div class="text-center text-sm text-gray-600">
            {{ uploadedCount }} of {{ totalCount }} uploaded
          </div>
        </div>

        <!-- Show only failed features -->
        <div v-if="failedCount > 0" class="space-y-2">
          <h4 class="text-sm font-medium text-red-600">Failed uploads</h4>
          <!-- Native Scrollable Area -->
          <div class="max-h-48 border border-red-200 rounded overflow-auto bg-red-50">
            <div class="p-2 space-y-1">
              <div
                v-for="[uuid, state] in uploadStates"
                v-show="state.status === 'error'"
                :key="uuid"
                class="flex items-center justify-between p-2 bg-white rounded text-sm"
              >
                <div class="flex items-center space-x-2 flex-1 min-w-0">
                  <XCircle class="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span class="truncate">
                    {{ state.name }}
                  </span>
                </div>
                <span class="text-xs text-red-600">
                  {{ state.error || 'Upload failed' }}
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>

      <DialogFooter>
        <Button
          variant="outline"
          @click="handleClose"
        >
          {{ isUploading ? 'Stop Uploading' : 'Close' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
